<script setup lang="ts">
import IconComplete from "@/icons/IconComplete.vue";
import {computed, onMounted, ref, toRefs, watch} from "vue";
import IconSoldOut from "@/icons/IconSoldOut.vue";
import moneyFormat from "../utils/moneyFormat";
import IconRemove from "@/icons/IconRemove.vue";
import IconDuplicate from "@/icons/IconDuplicate.vue";
import IconShipping from "@/icons/IconShipping.vue";
import IconEdit from "@/icons/IconEdit.vue";
import IconDate from "@/icons/IconDate.vue";
import GiftMessage from "@/components/GiftMessage.vue";
import IconConfirm from "@/icons/IconConfirm.vue";
import IconCheckbox from "@/icons/IconCheckbox.vue";
import {useCartStore, type LineItem, type LineItemWithAddressAndRates} from "@/stores/cartStore";
import IconDelivery from "@/icons/IconDelivery.vue";
import AddressForm from "@/components/AddressForm.vue";
import SelectDropdown from "@/components/SelectDropdown.vue";
import DatePicker from "@/components/DatePicker.vue";
import {dateToFormattedString, dateToString} from "@/utils/dateUtils";

const cartStore = useCartStore();
const properties = defineProps<{
  lineItem: LineItem,
  index: number
}>();
const { lineItem, index } = toRefs(properties);

const productUrl = `/products/${lineItem.value.handle}?variant=${lineItem.value.variant_id}`;
const useSameAddress = ref();
const useSameGiftMessage = ref();
const editAddress = ref(false);

const selectedDateText = computed(() => {
  if(lineItem.value.selectedRate && lineItem.value.selectedRate.estimatedDate) {
    if(Array.isArray(lineItem.value.selectedRate.estimatedDate)) {
      return dateToFormattedString(lineItem.value.selectedRate.estimatedDate[0], true) + " - " + dateToFormattedString(lineItem.value.selectedRate.estimatedDate[1], true);
    } else {
      return dateToFormattedString(lineItem.value.selectedRate.estimatedDate, true)
    }
  }

  return "-";
});

const addressOptions = computed(() => {
  return [
    {
      label: "New Address",
      value: "+",
      removable: false
    },
    ...cartStore.addresses.map(i => ({
      label: `${i.first_name} ${i.last_name}, ${i.address1}, ${i.address2 ? i.address2 + ', ' : ''} ${i.city}, ${i.province_code}, ${i.zip}, ${i.country}${i.phone ? ', ' + i.phone : ''}`,
      value: i.id,
      removable: true
    }))
  ];
});

const isPreviousLineItemsFilled = computed(() => {
  let allPrevIsFilled = false;
  for(const [index, item] of cartStore.lineItems.entries() ) {
    if(item.key === lineItem.value.key) {
      allPrevIsFilled = true;
      break;
    }
    if(!item.confirmed) {
      break;
    }
  }
  return allPrevIsFilled;
});

function handleEditAddress() {
  editAddress.value = !editAddress.value;
}

function handleRemoveProduct() {
  cartStore.removeProduct(lineItem.value.key);
}

function handleDuplicateProduct() {
  cartStore.duplicateProduct(lineItem.value.key);
}

function handleConfirmStep() {
  cartStore.setErrors(lineItem.value.key);

  if(!lineItem.value.selectedAddress || !lineItem.value.selectedRate) {
    return;
  }
  cartStore.confirmAddedInfo(
    lineItem.value.key,
    useSameAddress.value,
    useSameGiftMessage.value,
  );
}

function handleOpenSettings(step: number) {
  cartStore.editStep({
    key: lineItem.value.key,
    step
  });
}

const selectedAddressId = computed(() => lineItem.value.selectedAddress && lineItem.value.selectedAddress.id || '+');

watch(selectedAddressId, async (newValue) => {
  editAddress.value = newValue === "+";
});

const getDayWithOffset = (date: Date, moveDays = 0, skipDays = "delivery") => {
  const moveDirection = moveDays < 0 ? -1 : 1;
  const startDate = new Date(date);
  if(moveDays) {
    // move and skip weekends
    for(let i = 0; i < Math.abs(moveDays); i ++) {
      startDate.setDate(startDate.getDate() + moveDirection);
      while (
        startDate.getDay() === 0 ||
        ( skipDays === "weekends" || skipDays === "shipping" ) && startDate.getDay() === 6 ||
        skipDays === "shipping" && cartStore.disabled_shipping_dates.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
        skipDays === "delivery" && cartStore.disabled_delivery_dates.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
        skipDays === "delivery" && startDate.getDay() === 6 && (lineItem.value.weight <= 1 && lineItem.value.selectedAddress && lineItem.value.selectedAddress.type === "Commercial")
        )  {
        //skip weekends
        startDate.setDate(startDate.getDate() + moveDirection);
      }
    }
  } else {
    while(
      startDate.getDay() === 0 ||
      ( skipDays === "weekends" ||  skipDays === "shipping" ) && startDate.getDay() === 6  ||
      skipDays === "shipping" && cartStore.disabled_shipping_dates.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
      skipDays === "delivery" && cartStore.disabled_delivery_dates.findIndex( i => dateToString(startDate) === dateToString(i) ) !== -1 ||
      skipDays === "delivery" && startDate.getDay() === 6 && (lineItem.value.weight <= 1 && lineItem.value.selectedAddress && lineItem.value.selectedAddress.type === "Commercial")
      ) {
      startDate.setDate(startDate.getDate() + moveDirection);
    }
  }
  return startDate;
}

const firstAvailableDate = () => {
  const dateEST = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
  );

  const dateNow = new Date();

  const timeEST = dateEST.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  if(dateEST.getDay() === 0 || dateEST.getDay() === 6) {
    //if weekends don't calculate cut-off time
    return getDayWithOffset(dateNow, 0, "shipping");
  }

  if(timeEST > cartStore.cutoff_time) {
    return getDayWithOffset(dateNow, 2, "shipping");
  }

  return getDayWithOffset(dateNow, 1, "shipping");
}

const showDatePicker = computed(() => (lineItem.value.confirmed && (lineItem.value.editStep === 2 || lineItem.value.editStep === 1) || !lineItem.value.confirmed) && lineItem.value.selectedAddress && lineItem.value.rates && lineItem.value.rates.length);
const showGiftMessageForm = computed(() => (lineItem.value.confirmed && lineItem.value.editStep === 3 || !lineItem.value.confirmed));

onMounted(() => {
  if(selectedAddressId.value === '+') {
    editAddress.value = true;
  }
  if(lineItem.value.selectedRate && lineItem.value.selectedRate.shipDate) {
    const shipDate = dateToString(lineItem.value.selectedRate.shipDate);
    const firstAvailableShipDate = dateToString(firstAvailableDate())

    if(shipDate < firstAvailableShipDate || cartStore.disabled_shipping_dates.findIndex( i => shipDate === dateToString(i) ) !== -1) {
      cartStore.clearRates(lineItem.value.key);
    }
  }
});

</script>
<template>
  <div class="cart-product-box">
    <div class="top-line">
      <div class="gift">
        <template v-if="!lineItem.properties._gift_type || lineItem.properties._gift_type === '1'">
          <span class="gift-to">Myself -</span>
        </template>
        <template v-else-if="lineItem.properties && lineItem.properties._gift_type === '2'">
          <span class="gift-to">{{ lineItem.properties._gift_first_name }} {{ lineItem.properties._gift_last_name }} -</span>
        </template>
        <template v-else-if="lineItem.selectedAddress">
          <span class="gift-to">{{ lineItem.selectedAddress.first_name }} {{ lineItem.selectedAddress.last_name }} -</span>
        </template>
        Shipment <span class="gift-num">{{ index+1 }}</span>
      </div>
      <template v-if="lineItem.selectedRate">
        <div class="status status--complete">Complete <IconComplete :width="20" :height="20"/></div>
      </template>
      <template v-else-if="lineItem.selectedAddress">
        <div class="status status--fill">
          Provide Delivery Date
        </div>
      </template>
      <template v-else-if="!isPreviousLineItemsFilled">
        <div class="status status--error">Need More Information</div>
      </template>
      <template v-else>
        <div class="status status--fill">
          Provide Shipping Address
        </div>
      </template>
    </div>

    <div class="product-box-head product-box-head_checkout hidden-phone">
      <div class="ttl">Product</div>
      <div class="wrap">
        <div class="empty-info"></div>
        <div class="price">Price</div>
        <div class="quantity">Quantity</div>
        <div class="price price_subtotal">Subtotal</div>
        <div class="empty-remove"></div>
      </div>
    </div>

    <div class="product-box product-box_checkout">
      <div class="product-box-inner">
        <div class="pict">
          <div class="pict-quantity">{{ lineItem.quantity }}</div>
          <a :href="productUrl" :title="lineItem.product_title">
            <img class="CartItem__Image" :src="lineItem.featured_image.url" :alt="lineItem.product_title">
          </a>
        </div>
        <div class="product-info-wrap">
          <div class="product-info">
            <div class="u-h1 Heading">
              <a :href="productUrl">{{ lineItem.product_title }}</a>
            </div>
            <div class="sold-out is-hidden"><IconSoldOut/> Sold out</div>

            <template v-if="lineItem.variant_options.length && lineItem.variant_options[0] !== 'Default Title'">
              <p class="product-variant">{{ lineItem.variant_options.join(' / ') }}</p>
            </template>
          </div>
          <div class="product-price">
            <span class="CartItem__Price Price">{{ moneyFormat(lineItem.final_price, true) }}</span>
            <template v-if="lineItem.compare_at_price && lineItem.compare_at_price !== lineItem.price">
              <span class="CartItem__Price Price Price--compareAt">
                {{ moneyFormat(lineItem.compare_at_price, true) }}
              </span>
            </template>
          </div>
          <div class="product-quantity product-quantity--no-button">
            <div class="QuantitySelector">
              <input type="text" aria-label="Quantity input" readonly class="QuantitySelector__CurrentQuantity" :value="lineItem.quantity">
            </div>
          </div>
          <div class="product-price product-price_subtotal">
            <span class="CartItem__Price Price">{{ moneyFormat(lineItem.final_line_price, true) }}</span>
            <template v-if="lineItem.compare_at_price && lineItem.compare_at_price !== lineItem.price">
              <span class="CartItem__Price Price Price--compareAt">
                {{ moneyFormat(lineItem.compare_at_price*lineItem.quantity, true) }}
              </span>
            </template>
          </div>
          <div class="product-remove">
            <button type="button" @click="handleRemoveProduct" class="CartItem__Remove Link Link--underline Link--underlineShort" aria-label="Delete product"><IconRemove/></button>
          </div>
          <div class="product-gift">
            <div class="product-gift-line">
              <span class="duplicate">
                <a @click="handleDuplicateProduct" class="CartItem__Duplicate Link Link--underline Link--underlineShort">Duplicate Product</a>
                <span class="help" tabindex="0" data-toggle="tooltip">
                  <span class="tooltip fade top in">
                    <span class="tooltip-arrow"></span>
                    <span class="tooltip-inner">Use this to ship the same box to a different address.</span>
                  </span>
                  <IconDuplicate/>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <template v-if="isPreviousLineItemsFilled || lineItem.confirmed">
        <div class="product-settings">
          <template v-if="lineItem.confirmed && !lineItem.editStep">
            <div class="product-settings__head">
              <div class="product-settings__head-line">
                <div class="product-settings__head-icon"><IconShipping/></div>
                <div class="product-settings__head-ttl">Ship To:</div>
                <div class="product-settings__head-text" data-type="address-value">
                  <template v-if="lineItem.selectedAddress">
                    <strong>{{ lineItem.selectedAddress.first_name }} {{ lineItem.selectedAddress.last_name }}</strong>
                    {{ lineItem.selectedAddress.address1 }} {{ lineItem.selectedAddress.address2 ? lineItem.selectedAddress.address2 + " ," : "" }}
                    {{ lineItem.selectedAddress.city }}, {{ lineItem.selectedAddress.province_code }}, {{lineItem.selectedAddress.zip }}, {{lineItem.selectedAddress.country }}
                    {{ lineItem.selectedAddress.phone ? ', ' + lineItem.selectedAddress.phone : '' }}
                  </template>
                </div>
                <div class="product-settings__btn-edit btn-edit" @click="handleOpenSettings(1)"><IconEdit /></div>
              </div>
              <div class="product-settings__head-line">
                <div class="product-settings__head-icon"><IconDate /></div>
                <div class="product-settings__head-ttl">Delivery Date</div>
                <div class="product-settings__head-text">{{ selectedDateText }}</div>
                <div class="product-settings__btn-edit btn-edit" @click="handleOpenSettings(2)"><IconEdit /></div>
              </div>
              <div class="product-settings__head-line">
                <div class="product-settings__head-icon">
                  <svg width="20" height="20" aria-hidden="true" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="Icon Icon--gift ">
                    <path d="M18.75 5.61268H14.795C15.0788 5.41893 15.3225 5.22643 15.4925 5.05393C16.5 4.04018 16.5 2.39018 15.4925 1.37643C14.5138 0.391426 12.8063 0.390176 11.8263 1.37643C11.285 1.92018 9.84625 4.13393 10.045 5.61268H9.955C10.1525 4.13393 8.715 1.92018 8.17375 1.37643C7.19375 0.390176 5.48625 0.391426 4.5075 1.37643C3.5 2.39018 3.5 4.04018 4.50625 5.05393C4.6775 5.22643 4.92125 5.41893 5.205 5.61268H1.25C0.56125 5.61268 0 6.17393 0 6.86268V9.98768C0 10.3327 0.28 10.6127 0.625 10.6127H1.25V18.1127C1.25 18.8014 1.81125 19.3627 2.5 19.3627H17.5C18.1887 19.3627 18.75 18.8014 18.75 18.1127V10.6127H19.375C19.72 10.6127 20 10.3327 20 9.98768V6.86268C20 6.17393 19.4387 5.61268 18.75 5.61268ZM5.39375 2.25768C5.6475 2.00268 5.98375 1.86268 6.34125 1.86268C6.6975 1.86268 7.03375 2.00268 7.2875 2.25768C8.13 3.10518 8.9675 5.27018 8.67625 5.57143C8.67625 5.57143 8.62375 5.61268 8.445 5.61268C7.58125 5.61268 5.9325 4.71518 5.39375 4.17268C4.87 3.64518 4.87 2.78518 5.39375 2.25768ZM9.375 18.1127H2.5V10.6127H9.375V18.1127ZM9.375 9.36268H1.25V6.86268H8.445H9.375V9.36268ZM12.7125 2.25768C13.22 1.74893 14.1 1.75018 14.6063 2.25768C15.13 2.78518 15.13 3.64518 14.6063 4.17268C14.0675 4.71518 12.4188 5.61268 11.555 5.61268C11.3763 5.61268 11.3237 5.57268 11.3225 5.57143C11.0325 5.27018 11.87 3.10518 12.7125 2.25768ZM17.5 18.1127H10.625V10.6127H17.5V18.1127ZM18.75 9.36268H10.625V6.86268H11.555H18.75V9.36268Z" fill="currentColor"></path>
                  </svg>
                </div>
                <div class="product-settings__head-ttl">Gift Message</div>
                <div class="product-settings__head-text">{{ lineItem.properties.Message || "No Gift Message" }}</div>
                <div class="product-settings__btn-edit btn-edit" @click="handleOpenSettings(3)"><IconEdit /></div>
              </div>
            </div>
          </template>

          <div class="product-settings__body" v-if="!lineItem.confirmed || lineItem.editStep">
            <div class="panel">
              <div class="panel__head">
                <div class="panel__head-itm"><IconDelivery /> {{ lineItem.selectedAddress ? lineItem.selectedAddress.first_name + " " + lineItem.selectedAddress.last_name + " - " : "" }} Shipment {{ index + 1 }}</div>
              </div>
              <div class="panel__body">
                <div class="steps">
                  <template v-if="lineItem.confirmed && lineItem.editStep === 1 || !lineItem.confirmed">
                    <div class="steps__item">
                      <div class="steps__head">
                        <div class="steps__head-number">1</div>
                        <div class="steps__head-text">Enter Address or Select Saved Address</div>
                      </div>
                      <div class="steps__body steps__body--address form CartItem__Address">
                        <SelectDropdown :key="lineItem.key"
                                        :options="addressOptions"
                                        @remove-item="$event => { cartStore.removeAddress($event); }"
                                        :placeholder="'Select from saved address '"
                                        :modelValue="selectedAddressId"
                                        @update:modelValue="$event => { cartStore.selectAddress($event, lineItem.key )}"/>
                        <template v-if="selectedAddressId !== '+'">
                          <div class="form__group form__group--edit">
                            <label class="checkbox checkbox--txt-gray">
                              <input type="checkbox" class="checkbox__input" data-type="all-addresses" v-model="useSameAddress">
                              <span class="checkbox__icon"><IconCheckbox /></span>
                              Use this address for all shipments
                            </label>
                            <button type="button" class="lnk-edit" @click.stop="handleEditAddress">Edit address</button>
                          </div>
                        </template>
                        <template v-if="editAddress">
                          <AddressForm :key="lineItem.key" :selected-address="selectedAddressId" :line-item="lineItem" @close-address-form="_ => (editAddress = false)"/>
                        </template>
                      </div>
                    </div>
                  </template>

                  <template v-if="showDatePicker">
                    <DatePicker :key="lineItem.key" :line-item="lineItem as LineItemWithAddressAndRates" />
                  </template>

                  <template v-if="!showDatePicker && !lineItem.confirmed">
                    <div class="steps__item">
                      <div class="steps__head">
                        <div class="steps__head-number">2</div>
                        <div class="steps__head-text">Delivery Date</div>
                      </div>
                      <div class="steps__body steps__body--calendar">
                        <div class="select-date select-date--disabled">
                          <div class="select-date__text">Select <strong>DELIVERY DATE*</strong></div>
                          <div class="select-date__choice">
                            <div class="select-date__input">
                              Select delivery date
                            </div>
                            <IconDate />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-if="!lineItem.confirmed && (!showDatePicker && lineItem.selectedAddress && !cartStore.loading)">
                    <div class="Cart__no-shipping">
                      There are no shipping methods available for Your cart or address. Please make sure that the state or the rest of the address are correct.
                    </div>
                  </template>

                  <template v-if="showGiftMessageForm">
                    <div class="steps__item">
                      <div class="steps__head">
                        <div class="steps__head-number">3</div>
                        <div class="steps__head-text">Gift Message</div>
                      </div>

                      <div class="steps__body steps__body--gift">
                        <GiftMessage :key="lineItem.key" :line-item="lineItem" />

                        <label class="checkbox checkbox--txt-gray">
                          <input type="checkbox" class="checkbox__input" data-type="all-addresses" v-model="useSameGiftMessage">
                          <span class="checkbox__icon"><IconCheckbox /></span>
                          Use this message for all recipients.
                        </label>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="steps-confirm" v-if="lineItem.selectedAddress">
                  <button @click="handleConfirmStep" type="button" class="Button panel__button  Button--primary Button--full" aria-label="Confirm button">
                    <IconConfirm/> Confirm
                  </button>
                  <span class="form__label-error" :class="{ 'is-error': lineItem?.errors?.address || lineItem?.errors?.date }">* Please fill in all required fields to confirm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <p class="product-settings__error">
        This product is no longer available. Please remove it from your cart to continue.
      </p>
    </div>
  </div>
</template>